/**
 * Read-adapter from the real `payroll.listLeaveRequests` wire shape (plus a
 * narrow employee lookup) to the UI's `LeaveRequest` type (#23310). Pure, no
 * React — mirrors `wagepro_store_expenses.ts`'s `deriveExpensesFromRecords`
 * pattern.
 *
 * The backend row shape is a straight projection of the `leave_requests`
 * table (see `apps/api/src/db/schema/tenant/leave_requests.ts`): every field
 * this adapter reads is a plain TEXT column with no `CHECK` constraint (same
 * stance `wagepro_store_expenses.ts` documents), so an unrecognised value is
 * kept in the raw wire form rather than throwing. `employee_id` is joined
 * against `payroll.listEmployees`' `id` for the display name.
 */
import type { IsoDate } from './types';

/**
 * The recognised leave-type codes. `'casual'` was added as a named variant
 * (adversarial-review finding S6) so it renders with its own translated label
 * rather than collapsing to `'other'`. Any other unrecognised code is passed
 * through as a raw string and displayed via `t(key, { defaultValue: code })`.
 */
export type LeaveType = 'annual' | 'sick' | 'unpaid' | 'other' | 'casual';
/**
 * The four request statuses the backend's schema recognises.
 * `cancelled` was added in Phase 2 (`cancelLeave` procedure,
 * #wagepro-leave-phase2).
 */
export type LeaveStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

/**
 * The `leave_requests` wire fields this adapter reads — intentionally a
 * subset of the Drizzle row (columns like `approved_by` are audit-only and
 * never surfaced to the queue) so the shape stays stable across backend-only
 * column additions.
 */
export interface RealLeaveRequestRecord {
  id: string;
  employee_id: string;
  /** Raw wire string; `annual`/`sick`/`unpaid`/`other` in practice. */
  leave_type: string;
  start_date: IsoDate;
  end_date: IsoDate;
  /** Days requested scaled × 100 — same convention as `days_requested` in payroll.ts. */
  days_requested: number;
  /** Raw wire string; `pending`/`approved`/`rejected` in practice. */
  status: string;
  notes: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

/** The `payroll.listEmployees` fields this adapter reads (id → display name). */
export interface LeaveEmployeeLookup {
  id: string;
  full_name: string;
  /**
   * Worker type as stored (free TEXT, NULL = full-time). Optional because
   * this is a narrow projection of the `listEmployees` row that some call
   * sites build by hand with only id + name; absent reads as an employee.
   */
  employment_type?: string | null;
}

/**
 * The workers leave can be recorded for: everyone except independent
 * contractors, who accrue no leave (the API refuses `requestLeave` for them
 * with LEDGER_PAYROLL_CONTRACTOR_NOT_ELIGIBLE). Name resolution for
 * EXISTING requests must keep using the full list — a worker reclassified
 * as a contractor still has history.
 */
export function leave_eligible_employees(
  employees: readonly LeaveEmployeeLookup[],
): LeaveEmployeeLookup[] {
  return employees.filter((emp) => emp.employment_type !== 'contractor');
}

/** UI-facing leave request (all display-derived fields resolved). */
export interface LeaveRequest {
  id: string;
  employee_id: string;
  /** Resolved from `payroll.listEmployees`; `null` when the employee row is missing. */
  employee_name: string | null;
  /**
   * Raw wire leave-type code, passed through unchanged from the backend.
   * Well-known values (`annual`, `sick`, `unpaid`, `other`, `casual`) have
   * named translations in `LEAVE_TYPE_KEY`; any other code falls through to
   * a `t(key, { defaultValue: code })` display so custom leave types are
   * always readable — never silently shown as "Other".
   * (Adversarial-review finding S6.)
   */
  leave_type: string;
  start_date: IsoDate;
  end_date: IsoDate;
  /** Whole days (integer), rounded down from `days_requested / 100`. */
  days_requested_whole: number;
  /** Days with two-decimal precision — the raw scaled value ÷ 100, kept as a number so callers format it via the locale. */
  days_requested: number;
  status: LeaveStatus;
  notes: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

const LEAVE_STATUSES: readonly LeaveStatus[] = [
  'pending',
  'approved',
  'rejected',
  'cancelled',
];

/**
 * Pass the raw leave-type code through unchanged so the display layer can
 * render it with its own label via `t(key, { defaultValue: code })`. We no
 * longer collapse unknown codes to `'other'` — doing so silently loses
 * information and causes custom leave types (e.g. `'casual'`) to show as
 * "Other" in the employer queue. (Adversarial-review finding S6.)
 */
function normaliseLeaveType(raw: string): string {
  return raw;
}

function normaliseLeaveStatus(raw: string): LeaveStatus {
  return (LEAVE_STATUSES as readonly string[]).includes(raw)
    ? (raw as LeaveStatus)
    : 'pending';
}

/**
 * Adapts real wire rows into the UI shape. Pure and DI-testable — the
 * separate `records` and `employees` args mirror `deriveExpensesFromRecords`
 * exactly.
 */
export function deriveLeaveRequestsFromRecords(
  records: readonly RealLeaveRequestRecord[],
  employees: readonly LeaveEmployeeLookup[],
): LeaveRequest[] {
  const name_by_id = new Map(
    employees.map((emp) => [emp.id, emp.full_name] as const),
  );
  return records.map((record) => {
    const days = record.days_requested / 100;
    return {
      id: record.id,
      employee_id: record.employee_id,
      employee_name: name_by_id.get(record.employee_id) ?? null,
      leave_type: normaliseLeaveType(record.leave_type),
      start_date: record.start_date,
      end_date: record.end_date,
      days_requested_whole: Math.floor(days),
      days_requested: days,
      status: normaliseLeaveStatus(record.status),
      notes: record.notes,
      rejection_reason: record.rejection_reason,
      created_at: record.created_at,
      updated_at: record.updated_at,
    };
  });
}
